import { useState } from 'react';
import type { WorkOrderListItemDto } from '@signalvote/shared';
import {
  WorkOrderStatus,
  WorkOrderType,
  WorkOrderAction,
  WorkOrderBucket,
  WorkOrderCustomerGroup,
  workOrderBucket,
  workOrderCustomerGroup,
  operatorActionsFor,
  customerActionsFor,
  WORK_ORDER_BUCKET_LABELS,
  WORK_ORDER_BUCKET_ORDER,
  workOrderCustomerStatusLabel,
  WORK_ORDER_TYPE_LABELS,
  WORK_ORDER_CUSTOMER_GROUP_LABELS,
  WORK_ORDER_CUSTOMER_GROUP_ORDER,
  WORK_ORDER_ACTION_LABELS,
} from '@signalvote/shared';
import { Badge } from '@/components/ui/badge';
import { PendingButton } from '@/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { voterDataRecordsLabel } from './voterDataOrder';

/**
 * Actions consequential enough to confirm before firing — both are irreversible: Cancel voids the
 * Stripe invoice and terminally closes the send, Fail terminally marks it failed. Mirrors the mobile
 * jobs list's confirm set so the two clients guard the same actions. Non-destructive tail actions
 * (MarkVendorPaid / Complete) fire immediately.
 */
const DESTRUCTIVE_ACTIONS = new Set<WorkOrderAction>([WorkOrderAction.Cancel, WorkOrderAction.Fail]);

/** Confirmation copy per destructive action. The confirm verb avoids the double-"Cancel" trap. */
const CONFIRM_COPY: Record<string, { title: string; body: string; confirmLabel: string }> = {
  [WorkOrderAction.Cancel]: {
    title: 'Cancel this text send?',
    body: 'This cancels the send and voids its unpaid invoice. This can’t be undone.',
    confirmLabel: 'Cancel send',
  },
  [WorkOrderAction.Fail]: {
    title: 'Mark this text send as failed?',
    body: 'This marks the send as failed. This can’t be undone.',
    confirmLabel: 'Mark failed',
  },
};

interface WorkOrderJobsListProps {
  workOrders: WorkOrderListItemDto[];
  /**
   * Whether the viewer is a SignalVote platform admin (operator). Operators see the internal
   * status spine — the raw status badge, the awaiting-* buckets, and the hand-driven transition
   * buttons. Campaign customers (the default) see the friendly customer status/groups and never
   * the operator controls.
   */
  isPlatformAdmin?: boolean;
  /**
   * Whether the viewer may act on an order of this type (the per-type gate — a Text send needs
   * the campaign's Work Orders flag). A row the viewer can't act on shows no transition buttons.
   * Defaults to every type.
   */
  canActOn?: (type: WorkOrderType) => boolean;
  /** Invoked when the operator picks a hand-driven transition for a row (platform admins only). */
  onTransition?: (workOrder: WorkOrderListItemDto, action: WorkOrderAction) => void;
  /**
   * Invoked when a row is clicked/activated — the container opens that Work Order's detail. Every
   * row is clickable regardless of status; a Draft opens its editable build screen, a non-Draft a
   * read-only detail.
   */
  onOpen?: (workOrder: WorkOrderListItemDto) => void;
  /** The id of the row whose transition is currently in flight (locks that row). */
  pendingId?: string | null;
  /** A transition error to surface (e.g. a stale-version 409). */
  errorMessage?: string | null;
}

/**
 * The Work Orders jobs list. For a campaign customer (the default) every Work Order is grouped
 * under a friendly customer group (Drafts / Awaiting payment / Processing / Sent / Closed) with a
 * friendly status badge, and clicking any row opens its detail — customers never see the internal
 * spine or operator controls. For a SignalVote platform admin (`isPlatformAdmin`) the list keeps
 * the operator view: the awaiting-* buckets, the raw status badge, and the hand-driven transition
 * buttons the backend guard matrix allows from each status. A client-side group filter narrows the
 * view without a round-trip. The `group-<key>` / `filter-<key>` test ids use the group key.
 */
export function WorkOrderJobsList({
  workOrders,
  isPlatformAdmin = false,
  canActOn = () => true,
  onTransition,
  onOpen,
  pendingId,
  errorMessage,
}: WorkOrderJobsListProps) {
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  // A destructive action requested from a row, held until the admin confirms it (null = no dialog).
  const [pendingAction, setPendingAction] = useState<{
    workOrder: WorkOrderListItemDto;
    action: WorkOrderAction;
  } | null>(null);

  // Destructive actions (Cancel/Fail) open a confirm first; the rest fire immediately. Only wired
  // when the parent supplied onTransition (so a read-only list keeps its buttons disabled).
  const requestAction = onTransition
    ? (workOrder: WorkOrderListItemDto, action: WorkOrderAction) => {
        if (DESTRUCTIVE_ACTIONS.has(action)) setPendingAction({ workOrder, action });
        else onTransition(workOrder, action);
      }
    : undefined;

  const confirmPending = () => {
    if (pendingAction) onTransition?.(pendingAction.workOrder, pendingAction.action);
    setPendingAction(null);
  };

  const pendingCopy = pendingAction ? CONFIRM_COPY[pendingAction.action] : undefined;

  if (workOrders.length === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        No work orders yet.
      </p>
    );
  }

  // The grouping model swaps entirely on the viewer: operators see the internal awaiting-* buckets;
  // customers see the friendly customer groups. Both share the same key-driven render below, so the
  // `group-<key>` / `filter-<key>` test ids stay the group key in either mode.
  const groupOf = (wo: WorkOrderListItemDto): string =>
    isPlatformAdmin ? workOrderBucket(wo.status, wo.type) : workOrderCustomerGroup(wo.status, wo.type);
  const groupOrder: readonly string[] = isPlatformAdmin
    ? WORK_ORDER_BUCKET_ORDER
    : WORK_ORDER_CUSTOMER_GROUP_ORDER;
  const groupLabel = (key: string): string =>
    isPlatformAdmin
      ? WORK_ORDER_BUCKET_LABELS[key as WorkOrderBucket]
      : WORK_ORDER_CUSTOMER_GROUP_LABELS[key as WorkOrderCustomerGroup];

  const grouped = new Map<string, WorkOrderListItemDto[]>();
  for (const wo of workOrders) {
    const group = groupOf(wo);
    const existing = grouped.get(group);
    if (existing) existing.push(wo);
    else grouped.set(group, [wo]);
  }

  const visibleGroups = groupOrder.filter(
    (group) => (groupFilter === null || groupFilter === group) && (grouped.get(group)?.length ?? 0) > 0,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
        <FilterChip
          label="All"
          active={groupFilter === null}
          onClick={() => setGroupFilter(null)}
          testId="filter-all"
        />
        {groupOrder.map((group) => (
          <FilterChip
            key={group}
            label={groupLabel(group)}
            count={grouped.get(group)?.length ?? 0}
            active={groupFilter === group}
            onClick={() => setGroupFilter(group)}
            testId={`filter-${group}`}
          />
        ))}
      </div>

      {errorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      {visibleGroups.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          No work orders in this view.
        </p>
      ) : (
        visibleGroups.map((group) => (
          <section key={group} data-testid={`group-${group}`} className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {groupLabel(group)}
              <span className="ml-2 tabular-nums font-normal">{grouped.get(group)!.length}</span>
            </h3>
            <ul className="space-y-2">
              {grouped.get(group)!.map((wo) => (
                <WorkOrderRow
                  key={wo.id}
                  workOrder={wo}
                  isPlatformAdmin={isPlatformAdmin}
                  canAct={canActOn(wo.type)}
                  onAction={requestAction}
                  onOpen={onOpen}
                  pending={pendingId === wo.id}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        {pendingAction && pendingCopy && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{pendingCopy.title}</AlertDialogTitle>
              <AlertDialogDescription>{pendingCopy.body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Back</AlertDialogCancel>
              <AlertDialogAction onClick={confirmPending}>{pendingCopy.confirmLabel}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  testId: string;
}

function FilterChip({ label, count, active, onClick, testId }: FilterChipProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'border-input bg-background text-foreground hover:bg-accent',
      )}
    >
      {label}
      {count !== undefined && count > 0 && <span className="ml-1.5 tabular-nums">{count}</span>}
    </button>
  );
}

interface WorkOrderRowProps {
  workOrder: WorkOrderListItemDto;
  isPlatformAdmin: boolean;
  /** Whether the viewer may act on this row's type; false hides its transition buttons. */
  canAct: boolean;
  /** Requests a transition for this row — destructive actions are confirmed by the list first. */
  onAction?: (workOrder: WorkOrderListItemDto, action: WorkOrderAction) => void;
  onOpen?: (workOrder: WorkOrderListItemDto) => void;
  pending: boolean;
}

function WorkOrderRow({ workOrder, isPlatformAdmin, canAct, onAction, onOpen, pending }: WorkOrderRowProps) {
  // Operators see the type's full manual tail; a customer sees only their self-serve edges —
  // today just cancelling an unpaid invoiced order (ADR-068 choice 5), never the operator tail.
  const actions = !canAct
    ? []
    : isPlatformAdmin
      ? operatorActionsFor(workOrder.status, workOrder.type)
      : customerActionsFor(workOrder.status, workOrder.type);
  const schedule = formatSchedule(workOrder);

  // Operators keep the raw status name; customers see the friendly label.
  const statusLabel = isPlatformAdmin
    ? workOrder.status
    : workOrderCustomerStatusLabel(workOrder.status, workOrder.type);

  // The whole row opens the Work Order's detail (every status); an unpaid one pays by card there.
  // The operator buttons stop propagation so they don't also trigger the open.
  const handleOpen = () => onOpen?.(workOrder);
  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <li
      data-testid={`row-${workOrder.id}`}
      role="button"
      tabIndex={0}
      aria-label={`Open ${statusLabel} ${WORK_ORDER_TYPE_LABELS[workOrder.type].toLowerCase()}`}
      onClick={handleOpen}
      onKeyDown={handleRowKeyDown}
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-input p-3 text-left transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex min-w-0 flex-1 basis-60 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Badge
            data-testid={`status-${workOrder.id}`}
            variant={statusBadgeVariant(workOrder.status)}
          >
            {statusLabel}
          </Badge>
          <span className="text-sm tabular-nums">{formatAmount(workOrder.amount)}</span>
          {schedule && <span className="whitespace-nowrap text-sm text-muted-foreground">{schedule}</span>}
          <span className="whitespace-nowrap text-xs text-muted-foreground">Order {workOrder.reference}</span>
          {workOrder.voterData && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {voterDataRecordsLabel(workOrder.voterData.records, workOrder.voterData.onePerHousehold)}
            </span>
          )}
        </div>
        {workOrder.voterData?.sentence && (
          <p className="truncate text-sm">{workOrder.voterData.sentence}</p>
        )}
      </div>

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <PendingButton
              key={action}
              size="sm"
              variant={actionButtonVariant(action)}
              pending={pending}
              disabled={!onAction}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(workOrder, action);
              }}
            >
              {WORK_ORDER_ACTION_LABELS[action]}
            </PendingButton>
          ))}
        </div>
      )}
    </li>
  );
}

function statusBadgeVariant(status: WorkOrderStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case WorkOrderStatus.Complete:
      return 'default';
    case WorkOrderStatus.Failed:
      return 'destructive';
    case WorkOrderStatus.Cancelled:
      return 'secondary';
    case WorkOrderStatus.Draft:
    case WorkOrderStatus.SentToVendor:
    case WorkOrderStatus.VendorPaid:
    case WorkOrderStatus.Invoiced:
    case WorkOrderStatus.Paid:
    default:
      return 'outline';
  }
}

function actionButtonVariant(action: WorkOrderAction): 'default' | 'outline' | 'destructive' {
  switch (action) {
    case WorkOrderAction.MarkVendorPaid:
    case WorkOrderAction.Complete:
      return 'default';
    case WorkOrderAction.Fail:
      return 'destructive';
    case WorkOrderAction.Cancel:
      return 'outline';
  }
}

function formatAmount(amount: number | null): string {
  if (amount === null) return '—';
  return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatSchedule(workOrder: WorkOrderListItemDto): string | null {
  if (!workOrder.sendDate) return null;
  return workOrder.sendTime ? `${workOrder.sendDate} ${workOrder.sendTime}` : workOrder.sendDate;
}
